import React from 'react';
import cleanGeometry from 'helpers/cleanGeometry';
import alphanumSort from 'helpers/sorters';
import { getHeaderSortClassName } from 'helpers/tableSort';
import { Input, Table } from 'syngenta-digital-cropwise-react-ui-kit';
import FieldIcon from 'components/Icons/FieldIcon';
import TableActions from 'screens/Applications&WorkOrders/StepsLayout/Components/TableActions';
import CropZoneEditModal from 'screens/Applications&WorkOrders/StepsLayout/Components/PropertiesStep/components/CropZoneEditModal';
import { CropZoneRecord } from './types';
import { sortByFieldThenZone } from './helpers';
import useCropZonesTableState from './useCropZonesTableState';
import { PropertyNameCell, CropZoneNameCell, CropCell } from './cells';
import './index.css';

const PAGE_SIZE = 20;

export interface CropZonesTableProps<
  T extends CropZoneRecord = CropZoneRecord
> {
  data: T[];
  onTotalAcreageChange: (total: number) => void;
  onDataChange?: (data: T[]) => void;
  /** Root wrapper class — each caller keeps owning its own scoped CSS file. */
  className?: string;
  /** AW constrains the table body height; Plans doesn't — caller's choice. */
  scroll?: { y?: number | string };
  getDeleteButtonLabel?: (record: T) => string;
}

const CropZonesTable = <T extends CropZoneRecord = CropZoneRecord>({
  data,
  onTotalAcreageChange,
  onDataChange,
  className = 'crop-zones-table',
  scroll,
  getDeleteButtonLabel
}: CropZonesTableProps<T>): React.ReactElement => {
  const {
    tableData,
    sortedData,
    sortInfo,
    currentPage,
    editModalOpen,
    editingRow,
    selectedAreaType,
    isDropdownVisible,
    setIsDropdownVisible,
    getDisplayValue,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleAcreageBlur,
    handleCoverageBlur,
    deleteRow,
    openEditModal,
    closeEditModal,
    handleAreaTypeChange,
    handleModalSave,
    handleTableChange,
    handlePageChange
  } = useCropZonesTableState<T>(data, onTotalAcreageChange, onDataChange);

  const columns = [
    {
      render: (_: any, record: T) => (
        <div className="pr-2px">
          <FieldIcon
            id={record.id}
            features={
              record.geometry
                ? [
                    {
                      type: 'Feature',
                      geometry: cleanGeometry(record.geometry),
                      properties: {}
                    }
                  ]
                : []
            }
            border="#868CA2"
            color="#DFE2E7"
          />
        </div>
      ),
      width: 50
    },
    {
      title: `Crop Zones (${tableData.length})`,
      dataIndex: 'fieldName',
      key: 'name',
      sorter: (a: T, b: T) => {
        const sortedPair = sortByFieldThenZone([a, b], 'asc');
        return sortedPair[0] === a ? -1 : 1;
      },
      className: getHeaderSortClassName(
        'name',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'name' ? sortInfo.order : null,
      render: (_: any, record: T) => <CropZoneNameCell record={record} />
    },
    {
      title: 'Crop',
      dataIndex: 'crop',
      key: 'cropName',
      sorter: (a: T, b: T) => alphanumSort([a, b], 'crop.name'),
      className: getHeaderSortClassName(
        'cropName',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'cropName' ? sortInfo.order : null,
      render: (_: any, record: T) => <CropCell record={record} />
    },
    {
      title: 'Farm',
      dataIndex: 'propertyName',
      key: 'propertyName',
      sorter: (a: T, b: T) => alphanumSort([a, b], 'propertyName'),
      className: getHeaderSortClassName(
        'propertyName',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'propertyName' ? sortInfo.order : null,
      render: (_: any, record: T) => <PropertyNameCell record={record} />
    },
    {
      title: 'Applied Acreage',
      dataIndex: 'appliedAcreage',
      key: 'appliedAcreage',
      width: 158,
      render: (_: any, record: T) => {
        const originalIdx = tableData.findIndex(item => item.id === record.id);
        const displayValue = getDisplayValue(
          record.appliedAcreage,
          'acreage',
          originalIdx
        );
        return (
          <Input
            id={`appliedAcreage-${originalIdx}`}
            data-testid={`applied-acreage-${record.id}`}
            className="w-full"
            style={{ maxWidth: 'calc(100% - 16px)' }}
            value={displayValue}
            suffix={<span className="text-neutral-60">ac</span>}
            onFocus={() =>
              handleInputFocus('acreage', originalIdx, record.appliedAcreage)
            }
            onChange={e => {
              handleInputChange('acreage', originalIdx, e.target.value);
            }}
            onBlur={() => {
              handleInputBlur('acreage', originalIdx, handleAcreageBlur);
            }}
          />
        );
      }
    },
    {
      title: 'Coverage Percent',
      dataIndex: 'coveragePercent',
      key: 'coveragePercent',
      width: 154,
      render: (_: any, record: T) => {
        const originalIdx = tableData.findIndex(item => item.id === record.id);
        const displayValue = getDisplayValue(
          record.coveragePercent,
          'coverage',
          originalIdx
        );
        return (
          <Input
            id={`coveragePercent-${originalIdx}`}
            data-testid={`coverage-percent-${record.id}`}
            className="w-full"
            style={{ maxWidth: 'calc(100% - 16px)' }}
            type="text"
            inputMode="decimal"
            value={displayValue}
            suffix={<span className="text-neutral-60">%</span>}
            onFocus={() =>
              handleInputFocus('coverage', originalIdx, record.coveragePercent)
            }
            onChange={e => {
              handleInputChange('coverage', originalIdx, e.target.value);
            }}
            onBlur={() => {
              handleInputBlur('coverage', originalIdx, handleCoverageBlur);
            }}
          />
        );
      }
    },
    {
      title: '',
      key: 'actions',
      width: 107,
      render: (_: any, record: T) => {
        const { reportedArea } = record;
        const hasReportedArea =
          reportedArea !== undefined && reportedArea !== 0;
        // `idx` from antd's row render is relative to the sorted/paginated
        // page slice, not `tableData` — resolve the real index via `id` so
        // editing/deleting a row past page 1 (or under any active sort)
        // operates on the right zone.
        const originalIdx = tableData.findIndex(item => item.id === record.id);

        return (
          <TableActions
            showEdit={hasReportedArea}
            onEdit={() => openEditModal(originalIdx, record)}
            onDelete={() => deleteRow(originalIdx)}
            deleteButtonLabel={getDeleteButtonLabel?.(record)}
          />
        );
      }
    }
  ];

  return (
    <div className={`${className} flex flex-col`}>
      <Table
        rowKey="id"
        dataSource={sortedData}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: sortedData.length,
          onChange: handlePageChange,
          showSizeChanger: false,
          position: ['bottomRight'],
          showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total}`
        }}
        onChange={handleTableChange}
        scroll={scroll}
      />

      <CropZoneEditModal
        open={editModalOpen}
        editingRow={editingRow}
        selectedAreaType={selectedAreaType}
        isDropdownVisible={isDropdownVisible}
        onAreaTypeChange={handleAreaTypeChange}
        onDropdownVisibleChange={setIsDropdownVisible}
        onCancel={closeEditModal}
        onSave={handleModalSave}
      />
    </div>
  );
};

export default CropZonesTable;
